import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Col, Form, Nav, Row } from "react-bootstrap";
import ApiService, { DocumentData } from "../services/ApiService";
import UserAuthService from "../services/UserAuthService";
import { Event } from "../models/Event";
import { TemplateFile } from "../models/Templates";
import { useCreateWizard } from "../components/modals/createwizard/useCreateWizard";
import StepSelectEvent from "../components/modals/createwizard/StepSelectEvent";
import StepSelectTemplate from "../components/modals/createwizard/StepSelectTemplate";
import StepMapFields from "../components/modals/createwizard/StepMapFields";
import StepOutputSettings from "../components/modals/createwizard/StepOutputSettings";
import ModalLoading from "../components/modals/ModalLoading";
import TemplatePreviewDialog from "../components/TemplatePreviewDialog";
import ToastSuccess from "../components/ToastSuccess";
import ToastWarning from "../components/ToastWarning";
import { useTrialMode } from "../hooks/useTrialMode";
import { formatDate } from "../utils/dateUtils";
import logger from "../utils/logging";
import { extractPdfFieldWidgets, PdfFieldWidget } from "../utils/pdfFields";
import TemplatePreviewPane from "../components/templateEditor/PdfPreviewPane";
import { QrCodeSpec, TemplateFieldMeta } from "../models/TemplateMeta";
import { MissingPropertyError } from "../errors";
import { DocumentsCreateResponse } from "../models/Document";
import { WIZARD_STEPS } from "../components/modals/createwizard/useCreateWizard";
import "../css/CreateDocuments.css";
import "../css/TemplateEditor.css";
import PageHeader from "../components/PageHeader";

interface FetchError {
    error: any;
    message: string;
    key: string;
}

type UiStep = "select" | "edit";

interface CreateDocumentsProps {
    apiService: ApiService;
    authService: UserAuthService;
    webAppBaseUrl: string;
}

const CreateDocuments: React.FC<CreateDocumentsProps> = ({ apiService, authService, webAppBaseUrl }) => {
    const { t } = useTranslation(["wizard", "documents", "common", "pdfPreview"]);
    const navigate = useNavigate();
    const { isTrialMode } = useTrialMode(apiService, authService);

    const [uiStep, setUiStep] = React.useState<UiStep>("select");
    const [fetchErrors, setFetchErrors] = React.useState<FetchError[]>([]);
    const [templates, setTemplates] = React.useState<TemplateFile[] | null>(null);
    const [events, setEvents] = React.useState<Event[] | null>(null);
    const [progressMessage, setProgressMessage] = React.useState<string | null>(null);
    const [validationToastMessage, setValidationToastMessage] = React.useState<string | null>(null);
    const [successToastMessage, setSuccessToastMessage] = React.useState<string | null>(null);
    const [errorToastMessage, setErrorToastMessage] = React.useState<string | null>(null);
    const [previewTemplate, setPreviewTemplate] = React.useState<TemplateFile | null>(null);
    const [pdfBytes, setPdfBytes] = React.useState<ArrayBuffer | null>(null);
    const [qrCodes, setQrCodes] = React.useState<QrCodeSpec[]>([]);
    const [templatePdfBlobUrl, setTemplatePdfBlobUrl] = React.useState<string | null>(null);
    const [fieldWidgets, setFieldWidgets] = React.useState<PdfFieldWidget[]>([]);
    const [previewScale, setPreviewScale] = React.useState(1);
    const [showChangeConfirm, setShowChangeConfirm] = React.useState(false);
    const [editTab, setEditTab] = React.useState<"fields" | "output">("fields");
    const [selectedItem, setSelectedItem] = React.useState<{ type: "field" | "qr"; id: string } | null>(null);
    const [hoveredFieldName, setHoveredFieldName] = React.useState<string | null>(null);
    const [hoveredQrId, setHoveredQrId] = React.useState<string | null>(null);
    const selectedFieldName = selectedItem?.type === "field" ? selectedItem.id : null;
    const selectedQrId = selectedItem?.type === "qr" ? selectedItem.id : null;

    const {
        wizardState,
        setSelectedEvent,
        setSelectedTemplate,
        setFieldMappings,
        setQrCodeOverrides,
        setOutputSettings,
        setLiquidVariables,
        setEventGuestStatistic,
        getStepValidation,
        triggerValidation,
        resetWizard,
    } = useCreateWizard();

    const handleError = (error: any, message: string) => {
        const key = crypto.randomUUID();
        logger.error("handledError: " + message + ":", error);
        const normalizedErrorMessage = error instanceof Error ? error.message : String(error ?? "");

        setFetchErrors((previous) => {
            const alreadyPresent = previous.some((item) => {
                const existingErrorMessage =
                    item.error instanceof Error ? item.error.message : String(item.error ?? "");
                return item.message === message && existingErrorMessage === normalizedErrorMessage;
            });

            if (alreadyPresent) {
                return previous;
            }

            return [...previous, { error, message, key }];
        });
    };

    const removeError = (error: FetchError) => {
        setFetchErrors((previous) => previous.filter((item) => item.key !== error.key));
    };

    React.useEffect(() => {
        apiService
            .getTemplates()
            .then(setTemplates)
            .catch((error) => handleError(error, t("errors.loadTemplates")))
            .catch((error) => logger.error(error));
    }, [apiService, t]);

    React.useEffect(() => {
        apiService
            .getEvents(true)
            .then(setEvents)
            .catch((error) => handleError(error, t("errors.loadEvents")));
    }, [apiService, t]);

    React.useEffect(() => {
        if (!wizardState.selectedTemplate) {
            setPdfBytes(null);
            setFieldWidgets([]);
            setQrCodes([]);
            return;
        }

        setPdfBytes(null);
        setFieldWidgets([]);
        setQrCodes([]);
        setSelectedItem(null);
        setHoveredFieldName(null);

        let cancelled = false;

        const loadPdf = async () => {
            try {
                const blob = await apiService.getTemplatePdf(wizardState.selectedTemplate!.id);
                if (cancelled) return;
                const buffer = await blob.arrayBuffer();
                if (cancelled) return;
                setPdfBytes(buffer.slice(0));
                const widgets = await extractPdfFieldWidgets(buffer.slice(0));
                if (!cancelled) {
                    setFieldWidgets(widgets);
                }
            } catch (error) {
                if (!cancelled) {
                    logger.error("Failed to load template PDF", error);
                }
            }
        };

        void loadPdf();

        return () => {
            cancelled = true;
        };
    }, [apiService, wizardState.selectedTemplate]);

    React.useEffect(() => {
        if (!wizardState.selectedTemplate) {
            setQrCodes([]);
            return;
        }

        let cancelled = false;

        const loadTemplateMeta = async () => {
            try {
                const meta = await apiService.getTemplateMeta(wizardState.selectedTemplate!.id);
                if (!cancelled) {
                    setQrCodes(meta.qrCodes ?? []);
                }
            } catch (error) {
                logger.error("Failed to load template metadata for QR preview", error);
                if (!cancelled) {
                    setQrCodes([]);
                }
            }
        };

        void loadTemplateMeta();

        return () => {
            cancelled = true;
        };
    }, [apiService, wizardState.selectedTemplate]);

    React.useEffect(() => {
        if (!pdfBytes) {
            setTemplatePdfBlobUrl(null);
            return;
        }

        const blobUrl = URL.createObjectURL(new Blob([pdfBytes.slice(0)], { type: "application/pdf" }));
        setTemplatePdfBlobUrl(blobUrl);

        return () => {
            URL.revokeObjectURL(blobUrl);
        };
    }, [pdfBytes]);

    const handleEventChange = async (event: Event | null | undefined) => {
        if (event == null) {
            setLiquidVariables(null);
            setEventGuestStatistic(null);
            return;
        }

        if (wizardState.outputSettings.documentName == null) {
            setOutputSettings({ documentName: event.name + " (" + formatDate(new Date()) + ")" });
        }

        apiService
            .getEventInfo(event.id)
            .then((data) => {
                setLiquidVariables(data.liquidVariables);
            })
            .catch((error) => {
                setLiquidVariables(null);
                handleError(error, t("errors.loadVariables"));
            });

        apiService
            .getEventGuestStatistic(event.id)
            .then((data) => {
                setEventGuestStatistic(data);
            })
            .catch((error) => {
                setEventGuestStatistic(null);
                handleError(error, t("errors.loadStatistics"));
            });
    };

    const handleEventSelection = (event: Event | null) => {
        setSelectedEvent(event);
        handleEventChange(event);
    };

    const handleTemplateSelection = (template: TemplateFile | null) => {
        setSelectedTemplate(template);
    };

    const handleTemplatePreview = (template: TemplateFile) => {
        setPreviewTemplate(template);
    };

    const handlePreviewClose = () => {
        setPreviewTemplate(null);
    };

    const focusMappingInput = React.useCallback((attrName: string, attrValue: string) => {
        const tryFocus = (attempt: number) => {
            const section = document.querySelector(".step-map-fields");
            const target = section?.querySelector<HTMLElement>(`[${attrName}="${CSS.escape(attrValue)}"]`);
            if (!target) {
                if (attempt < 5) requestAnimationFrame(() => tryFocus(attempt + 1));
                return;
            }
            target.scrollIntoView({ behavior: "smooth", block: "nearest" });
            target.focus({ preventScroll: true });
        };
        requestAnimationFrame(() => tryFocus(1));
    }, []);

    const handlePreviewFieldSelect = React.useCallback(
        (fieldName: string) => {
            setSelectedItem({ type: "field", id: fieldName });
            setEditTab("fields");
            focusMappingInput("data-templatefieldname", fieldName);
        },
        [focusMappingInput],
    );

    const handlePreviewQrSelect = React.useCallback(
        (qrId: string) => {
            setSelectedItem({ type: "qr", id: qrId });
            setEditTab("fields");
            focusMappingInput("data-qrcodeid", qrId);
        },
        [focusMappingInput],
    );

    const selectedEventUrl = React.useMemo(() => {
        if (!wizardState.selectedEvent) {
            return null;
        }

        return `${webAppBaseUrl.replace(/\/$/, "")}/cms/event_hub/${wizardState.selectedEvent.id}`;
    }, [webAppBaseUrl, wizardState.selectedEvent]);

    const canProceedSelect =
        getStepValidation(WIZARD_STEPS.SELECT_EVENT).canProceedFrom &&
        getStepValidation(WIZARD_STEPS.SELECT_TEMPLATE).canProceedFrom;

    const scrollToFirstValidationIssue = React.useCallback((tab: "fields" | "output") => {
        requestAnimationFrame(() => {
            const sectionSelector = tab === "output" ? ".step-output-settings" : ".step-map-fields";
            const section = document.querySelector(sectionSelector);
            if (!section) {
                return;
            }

            const target = section.querySelector(
                ".has-validation-error, .is-invalid, .text-warning, .text-danger",
            ) as HTMLElement | null;
            if (!target) {
                return;
            }

            target.scrollIntoView({ behavior: "smooth", block: "center" });

            const focusable = target.matches("input, select, textarea, button, [tabindex]")
                ? target
                : (target.querySelector("input, select, textarea, button, [tabindex]") as HTMLElement | null);
            focusable?.focus({ preventScroll: true });
        });
    }, []);

    const handleContinue = () => {
        if (!canProceedSelect) {
            triggerValidation(WIZARD_STEPS.SELECT_EVENT);
            triggerValidation(WIZARD_STEPS.SELECT_TEMPLATE);
            setValidationToastMessage(t("validation.validationFailed"));
            return;
        }
        setUiStep("edit");
    };

    const handleBackToSelect = () => {
        setShowChangeConfirm(true);
    };

    const confirmChangeSelection = () => {
        resetWizard();
        setUiStep("select");
        setShowChangeConfirm(false);
    };

    const cancelChangeSelection = () => {
        setShowChangeConfirm(false);
    };

    const previewFieldMeta: TemplateFieldMeta[] = React.useMemo(() => {
        const fields = wizardState.selectedTemplate?.fields ?? [];
        return fields.map((field) => ({
            name: field.name,
            alternativeName: field.alternativeName ?? null,
            defaultValue: field.defaultValue ?? null,
        }));
    }, [wizardState.selectedTemplate]);

    const handleSubmit = (state = wizardState) => {
        const mapValidation = getStepValidation(WIZARD_STEPS.MAP_FIELDS);
        const outputValidation = getStepValidation(WIZARD_STEPS.OUTPUT_SETTINGS);

        if (!mapValidation.canProceedFrom || !outputValidation.canProceedFrom) {
            triggerValidation(WIZARD_STEPS.MAP_FIELDS);
            triggerValidation(WIZARD_STEPS.OUTPUT_SETTINGS);
            setValidationToastMessage(t("validation.validationFailed"));

            if (!outputValidation.canProceedFrom) {
                setEditTab("output");
                scrollToFirstValidationIssue("output");
            } else {
                setEditTab("fields");
                scrollToFirstValidationIssue("fields");
            }

            return;
        }

        setProgressMessage(t("loading.creatingDocuments"));

        if (!state.selectedEvent) {
            throw new MissingPropertyError("wizardState", "selectedEvent");
        }
        if (!state.selectedTemplate) {
            throw new MissingPropertyError("wizardState", "selectedTemplate");
        }

        const eventId = state.selectedEvent.id;
        const pdfTemplateId = state.selectedTemplate.id;
        const templateFields = state.fieldMappings;
        const qrCodeOverrides = state.qrCodeOverrides;
        const outputSettings = state.outputSettings;

        sendData(eventId, pdfTemplateId, templateFields, qrCodeOverrides, outputSettings)
            .then((progressInfo) => {
                logger.info("Document creation started:", progressInfo);
                fetchProgress(progressInfo.batchId);
            })
            .catch((error) => {
                setErrorToastMessage(error.message);
                setProgressMessage(null);
            });
    };

    const fetchProgress = (batchId: string) => {
        if (!batchId) {
            logger.error("error batchId missing");
            return;
        }

        apiService
            .getDocumentProgress(batchId)
            .then((progress) => {
                if (progress.state === "RUNNING") {
                    const message = t("loading.creating", { progress: progress.progress, size: progress.size });
                    setProgressMessage(message);
                    setTimeout(() => fetchProgress(batchId), 500);
                } else if (progress.state === "FINISHED" || progress.state === "COMPLETED_WITH_ERRORS") {
                    deleteProgress(batchId);
                    setProgressMessage(null);
                    setSuccessToastMessage(t("progress.finished"));
                    navigate(`/documents?fid=${batchId}`);
                } else if (progress.state === "FAILED") {
                    setErrorToastMessage(t("progress.failed"));
                    setProgressMessage(null);
                }
            })
            .catch((error) => {
                logger.error("Error fetching progress:", error);
                setProgressMessage(null);
            });
    };

    const deleteProgress = (progressId: string) => {
        if (!progressId) {
            logger.error("error progressId missing");
            return;
        }

        apiService.deleteDocumentProgress(progressId).catch((error) => {
            logger.error("Error deleting progress:", error);
        });
    };

    const sendData = async (
        eventId: string,
        pdfTemplateId: string,
        templateFields: Record<string, string>,
        qrCodeOverrides: Record<string, string> | null,
        outputSettings: DocumentData["outputSettings"],
    ): Promise<DocumentsCreateResponse> => {
        try {
            const documentData: DocumentData = {
                eventId: eventId,
                templateId: pdfTemplateId,
                replacementConfig: templateFields,
                qrCodeContentOverrides: qrCodeOverrides,
                outputSettings: outputSettings,
            };

            return await apiService.createDocuments(documentData);
        } catch (error) {
            logger.error("Error creating documents:", error);
            setProgressMessage(null);
            throw error;
        }
    };

    return (
        <>
            <PageHeader title={t("title")} subtitle={t("description")}>
                {uiStep === "edit" && (
                    <div className="text-end">
                        <div className="small text-muted mb-2">
                            {wizardState.selectedEvent ? (
                                selectedEventUrl ? (
                                    <a
                                        href={selectedEventUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-underline"
                                    >
                                        {wizardState.selectedEvent.name}
                                    </a>
                                ) : (
                                    <span>{wizardState.selectedEvent.name}</span>
                                )
                            ) : null}{" "}
                            ·{" "}
                            {wizardState.selectedTemplate ? (
                                templatePdfBlobUrl ? (
                                    <a
                                        href={templatePdfBlobUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-underline"
                                    >
                                        {wizardState.selectedTemplate.name}
                                    </a>
                                ) : (
                                    <span>{wizardState.selectedTemplate.name}</span>
                                )
                            ) : null}
                        </div>
                        <Button variant="secondary" size="sm" onClick={handleBackToSelect}>
                            {t("actions.changeSelection")}
                        </Button>
                    </div>
                )}
            </PageHeader>

            {uiStep === "select" && (
                <div className="create-documents-select">
                    <Row className="g-4">
                        <Col lg={6}>
                            <div className="create-documents-card">
                                <StepSelectEvent
                                    events={events}
                                    selectedEvent={wizardState.selectedEvent}
                                    eventGuestStatistic={wizardState.eventGuestStatistic}
                                    onEventChange={handleEventSelection}
                                    validation={
                                        getStepValidation(WIZARD_STEPS.SELECT_EVENT).fieldValidations.selectedEvent
                                    }
                                />
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="create-documents-card">
                                <StepSelectTemplate
                                    templates={templates}
                                    selectedTemplate={wizardState.selectedTemplate}
                                    onTemplateChange={handleTemplateSelection}
                                    onTemplatePreview={handleTemplatePreview}
                                    validation={
                                        getStepValidation(WIZARD_STEPS.SELECT_TEMPLATE).fieldValidations
                                            .selectedTemplate
                                    }
                                />
                            </div>
                        </Col>
                    </Row>
                    {fetchErrors.length > 0 && (
                        <Row className="mt-3">
                            <Col>
                                <Alert
                                    key={fetchErrors[0].key}
                                    variant="danger"
                                    onClose={() => removeError(fetchErrors[0])}
                                    dismissible
                                    className="mb-0"
                                >
                                    <h5>{fetchErrors[0].message}</h5>
                                    <p className="mb-0">
                                        {fetchErrors[0].error?.message ?? String(fetchErrors[0].error)}
                                    </p>
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="primary" onClick={handleContinue} disabled={!canProceedSelect}>
                            {t("actions.continue")}
                        </Button>
                    </div>
                </div>
            )}

            {uiStep === "edit" && (
                <Row className="g-4">
                    <Col lg={8}>
                        <div className="create-documents-preview-card">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="fw-semibold">{t("preview.title")}</div>
                                <Form.Select
                                    size="sm"
                                    className="create-documents-zoom"
                                    value={previewScale}
                                    onChange={(event) => setPreviewScale(Number(event.target.value))}
                                >
                                    <option value={0.75}>{t("preview.zoomSmall")}</option>
                                    <option value={1}>{t("preview.zoomNormal")}</option>
                                    <option value={1.25}>{t("preview.zoomLarge")}</option>
                                </Form.Select>
                            </div>
                            <div className="create-documents-preview-scroll">
                                <TemplatePreviewPane
                                    pdfBytes={pdfBytes}
                                    fieldWidgets={fieldWidgets}
                                    fieldMeta={previewFieldMeta}
                                    qrCodes={qrCodes}
                                    showFieldNames={true}
                                    showQrCodes={true}
                                    selectedQrId={selectedQrId}
                                    selectedFieldName={selectedFieldName}
                                    hoveredFieldName={hoveredFieldName}
                                    hoveredQrId={hoveredQrId}
                                    scale={previewScale}
                                    allowSelectionHighlight={true}
                                    onSelectField={handlePreviewFieldSelect}
                                    onSelectQr={handlePreviewQrSelect}
                                    onHoverField={setHoveredFieldName}
                                    onHoverQr={setHoveredQrId}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="create-documents-card" onClick={() => setSelectedItem(null)}>
                            <Nav
                                variant="tabs"
                                activeKey={editTab}
                                onSelect={(eventKey) => {
                                    if (eventKey === "fields" || eventKey === "output") {
                                        setEditTab(eventKey);
                                    }
                                }}
                                className="mb-3"
                            >
                                <Nav.Item>
                                    <Nav.Link eventKey="fields">{t("tabs.fields")}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="output">{t("tabs.output")}</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            {editTab === "fields" ? (
                                <StepMapFields
                                    template={wizardState.selectedTemplate}
                                    event={wizardState.selectedEvent}
                                    liquidVariables={wizardState.liquidVariables}
                                    fieldMappings={wizardState.fieldMappings}
                                    onFieldMappingsChange={setFieldMappings}
                                    qrCodes={qrCodes}
                                    qrCodeOverrides={wizardState.qrCodeOverrides}
                                    onQrCodeOverridesChange={setQrCodeOverrides}
                                    onQrFocus={(id) => setSelectedItem({ type: "qr", id })}
                                    onQrHover={setHoveredQrId}
                                    onFieldFocus={(id) => setSelectedItem({ type: "field", id })}
                                    onFieldHover={setHoveredFieldName}
                                    validation={
                                        getStepValidation(WIZARD_STEPS.MAP_FIELDS).fieldValidations.fieldMappings
                                    }
                                />
                            ) : (
                                <StepOutputSettings
                                    outputSettings={wizardState.outputSettings}
                                    onOutputSettingsChange={setOutputSettings}
                                    selectedEvent={wizardState.selectedEvent}
                                    eventGuestStatistic={wizardState.eventGuestStatistic}
                                    validation={getStepValidation(WIZARD_STEPS.OUTPUT_SETTINGS)}
                                    isTrialMode={isTrialMode}
                                />
                            )}
                        </div>
                        <div className="d-flex justify-content-end mt-4">
                            <Button variant="primary" onClick={() => handleSubmit(wizardState)}>
                                {t("navigation.generate")}
                            </Button>
                        </div>
                    </Col>
                </Row>
            )}
            {validationToastMessage && (
                <ToastWarning
                    show={validationToastMessage !== null}
                    message={validationToastMessage}
                    onClose={() => setValidationToastMessage(null)}
                />
            )}

            <ToastSuccess
                show={successToastMessage !== null}
                message={successToastMessage || ""}
                onClose={() => setSuccessToastMessage(null)}
            />

            <ToastWarning
                show={errorToastMessage !== null}
                message={errorToastMessage || ""}
                onClose={() => setErrorToastMessage(null)}
            />

            {progressMessage && <ModalLoading show={true} title={t("loading.title")} message={progressMessage} />}

            <TemplatePreviewDialog
                show={previewTemplate !== null}
                template={previewTemplate}
                apiService={apiService}
                onClose={handlePreviewClose}
            />

            {showChangeConfirm && <div className="modal-backdrop show" />}
            {showChangeConfirm && (
                <div className="create-documents-confirm">
                    <div className="create-documents-confirm-card">
                        <h5>{t("actions.changeTitle")}</h5>
                        <p className="text-muted small mb-4">{t("actions.changeDescription")}</p>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={cancelChangeSelection}>
                                {t("common:buttons.cancel")}
                            </Button>
                            <Button variant="danger" onClick={confirmChangeSelection}>
                                {t("actions.changeConfirm")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CreateDocuments;
